import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to fetch daily aggregated credit usage
 * Returns usage grouped by day for simple history view
 */
export function useDailyCredits() {
  const [dailyUsage, setDailyUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekTotal, setWeekTotal] = useState(0);

  useEffect(() => {
    fetchDailyUsage();
  }, []);

  async function fetchDailyUsage() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      // Fetch last 30 days of usage
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('mellow_credit_usage_logs')
        .select('credits_used, created_at')
        .eq('user_id', user.id)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching daily credits:', error);
        setLoading(false);
        return;
      }

      // Group by day
      const dailyMap = new Map();
      
      (data || []).forEach(log => {
        const date = new Date(log.created_at);
        const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        
        if (!dailyMap.has(dayKey)) {
          dailyMap.set(dayKey, {
            date: dayKey,
            credits: 0,
            timestamp: date,
          });
        }
        
        dailyMap.get(dayKey).credits += log.credits_used || 0;
      });

      // Convert to array and sort by date descending
      const dailyArray = Array.from(dailyMap.values())
        .sort((a, b) => b.timestamp - a.timestamp);

      // Calculate last 7 days total
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const weekSum = dailyArray
        .filter(day => day.timestamp >= sevenDaysAgo)
        .reduce((sum, day) => sum + day.credits, 0);

      setDailyUsage(dailyArray);
      setWeekTotal(weekSum);
      setLoading(false);
    } catch (err) {
      console.error('Error in fetchDailyUsage:', err);
      setLoading(false);
    }
  }

  return { dailyUsage, weekTotal, loading, refresh: fetchDailyUsage };
}

export default useDailyCredits;

